import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import { useEffect, useMemo, useState } from 'react';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { MultiSelectTree, TreeNode } from '../../forms/MultiSelectTree';
import { uniq } from 'lodash';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';
import { TransportationService } from '../../../data/models/transportation-service.model';
import * as uiSlice from '../../../data/ui.slice';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ServiceLevelModelDialog';

const classes = {
  content: `${PREFIX}-content`,
  nameInput: `${PREFIX}-nameInput`,
  actions: `${PREFIX}-actions`,
  actionButton: `${PREFIX}-actionButton`,
  buttons: `${PREFIX}-buttons`,
};

interface RootDialogProps {
  dialogHeight?: number;
}

const RootDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'dialogHeight',
})<RootDialogProps>(({ theme, dialogHeight }) => ({
  '& .MuiDialog-paper': {
    width: 600,
    height: dialogHeight ?? 'auto',
  },
  '& .MuiDialogTitle-root': {
    ...theme.typography.h3,
    paddingBottom: theme.spacing(1),
  },
  [`& .${classes.content}`]: {
    paddingTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.nameInput}`]: {
    width: 382,
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'end',
  },
  [`& .${classes.actionButton}`]: {
    width: 178,
    height: 48,
  },
  [`& .${classes.buttons}`]: {
    display: 'flex',
    gap: theme.spacing(3),
  },
}));

export enum ServiceLevelModelDialogAction {
  Add = 'add',
  Edit = 'edit',
  Duplicate = 'duplicate',
  View = 'view',
}

interface ServiceLevelModelDialogProps {
  open: boolean;
  close: () => void;
  action?: ServiceLevelModelDialogAction;
  serviceLevelModel?: TransportationService;
}

export default function ServiceLevelModelDialog({
  open,
  close,
  action,
  serviceLevelModel,
}: ServiceLevelModelDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [isSelectExpanded, setIsSelectExpanded] = useState(false);

  const isSuperReaderUser = useIsSuperReaderUser();

  const requiredFieldMessage = t('common.requiredField');

  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const saving = useAppSelector(
    accountingSlice.selectSavingTransportationService,
  );
  const saved = useAppSelector(
    accountingSlice.selectSavedTransportationService,
  );
  const isReadOnly =
    action === ServiceLevelModelDialogAction.View || isSuperReaderUser;

  const childCategories = uniq(
    transportationAttributes.map(
      (ta) => ta.childTransportationAttributeCategoryBC,
    ),
  );

  const rootTransAttrbs = transportationAttributes.filter(
    (ta) => !childCategories.includes(ta.transportationAttributeCategoryBC),
  );

  const getTreeNode = (
    attrb: TransportationAttribute,
    parentLabel?: string,
    parentId?: string,
  ) => {
    const selectedLabel = parentLabel
      ? `${parentLabel} - ${attrb.name}`
      : attrb.name;
    const id = parentId
      ? `${parentId}#${attrb.transportationAttributeUUID}`
      : (attrb.transportationAttributeUUID ?? '');
    return {
      id,
      label: attrb.name,
      selectedLabel,
      value: id,
      children: getChildAttributes(attrb, selectedLabel, id),
    };
  };

  const getChildAttributes = (
    attrb: TransportationAttribute,
    parentLabel?: string,
    parentId?: string,
  ) => {
    const children = transportationAttributes.filter(
      (ta) =>
        ta.transportationAttributeCategoryBC ===
        attrb.childTransportationAttributeCategoryBC,
    );
    const treeNodes: TreeNode[] = children.map((child) =>
      getTreeNode(child, parentLabel, parentId),
    );
    return treeNodes;
  };

  const transAttrbTree: TreeNode[] = rootTransAttrbs.map((attrb) => {
    return getTreeNode(attrb);
  });

  const formHook = useForm({
    defaultValues: {
      id: '',
      name: '',
      transportationAttributes: [] as string[],
    },
  });

  useEffect(() => {
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  useEffect(() => {
    if (saved && saveSuccessMessage) {
      handleClose();
    }
  }, [saved, saveSuccessMessage]);

  useEffect(() => {
    if (
      action === ServiceLevelModelDialogAction.Edit ||
      action === ServiceLevelModelDialogAction.Duplicate ||
      action === ServiceLevelModelDialogAction.View
    ) {
      formHook.setValue(
        'id',
        serviceLevelModel?.transportationServiceUUID || '',
      );
      let name = serviceLevelModel?.name ?? '';
      if (action === ServiceLevelModelDialogAction.Duplicate) {
        name = t('serviceLevelModels.duplicateOf', { name });
      }
      formHook.setValue('name', name);
      const transportationAttributes =
        serviceLevelModel?.transportationAttributes.map((arr) => arr.join('#'));
      formHook.setValue(
        'transportationAttributes',
        transportationAttributes ?? [],
      );

      if (action === ServiceLevelModelDialogAction.Duplicate) {
        // For duplication, allow the user to save if untouched
        formHook.trigger();
      }
    } else {
      formHook.reset();
    }
  }, [serviceLevelModel, action]);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleSave = (data: any) => {
    const attrs: string[][] = data.transportationAttributes.map(
      (attr: string) => attr.split('#'),
    );
    const serviceLevelModel: TransportationService = {
      transportationServiceUUID: data.id || undefined,
      name: data.name,
      transportationAttributes: attrs,
    };
    if (action === ServiceLevelModelDialogAction.Edit) {
      dispatch(
        accountingSlice.updateTransportationServiceAsync(serviceLevelModel),
      );
    } else {
      if (action === ServiceLevelModelDialogAction.Duplicate) {
        serviceLevelModel.transportationServiceUUID = undefined;
      }
      dispatch(
        accountingSlice.createTransportationServiceAsync(serviceLevelModel),
      );
    }
  };

  const saveActionButtonLabel = useMemo(() => {
    switch (action) {
      case ServiceLevelModelDialogAction.Add:
        return t('common.add');
      case ServiceLevelModelDialogAction.Duplicate:
        return t('common.duplicate');
      default:
        return t('common.save');
    }
  }, [action]);

  const dialogHeight = useMemo(() => {
    if (action === ServiceLevelModelDialogAction.View) {
      return 524;
    }
    return isSelectExpanded ? 625 : 516;
  }, [action, isSelectExpanded]);

  return (
    <RootDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={true}
      dialogHeight={dialogHeight}>
      <DialogTitle>
        {t(`serviceLevelModels.${action}ServiceLevelModel`)}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" className={classes.content}>
          <TextInput
            name="name"
            label={t('serviceLevelModels.name')}
            className={classes.nameInput}
            control={formHook.control}
            required={!isReadOnly}
            rules={{ required: requiredFieldMessage }}
            errors={formHook.formState.errors}
            readOnly={isReadOnly}
          />
          <MultiSelectTree
            name="transportationAttributes"
            label={t('serviceLevelModels.transportationAttributes')}
            treeData={transAttrbTree}
            control={formHook.control}
            readOnly={isReadOnly}
            required={!isReadOnly}
            onExpandChange={setIsSelectExpanded}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Box className={classes.buttons}>
          {isReadOnly && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.actionButton}
              onClick={handleClose}>
              {t('common.close')}
            </Button>
          )}
          {!isReadOnly && (
            <>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                className={classes.actionButton}
                onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                className={classes.actionButton}
                disabled={!formHook.formState.isValid || saving}
                onClick={formHook.handleSubmit(handleSave)}>
                {saveActionButtonLabel}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </RootDialog>
  );
}
